import React, { useState } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { Play, X } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

const VideoPresentations = () => {
  const { language } = useLanguage();
  const [selectedVideo, setSelectedVideo] = useState(null);

  const videos = [
    {
      id: 'investment',
      title: language === 'fr' ? 'Opportunités d\'Investissement en RDC' : 'DRC Investment Opportunities',
      description: language === 'fr' 
        ? 'Découvrez les opportunités exceptionnelles d\'investissement en République Démocratique du Congo.'
        : 'Discover the exceptional investment opportunities in the Democratic Republic of Congo.',
      thumbnail: 'https://images.unsplash.com/photo-1633960719106-894ffd8b34d2?crop=entropy&cs=srgb&fm=jpg&q=85&w=800',
      videoUrl: '/fbg-investment.mp4',
      language: 'en'
    },
    {
      id: 'investment-turkish',
      title: language === 'fr' ? 'Opportunités d\'Investissement (Turc)' : 'Investment Opportunities (Turkish)',
      description: language === 'fr' 
        ? 'Présentation en turc des opportunités d\'investissement en RDC.'
        : 'Turkish presentation of DRC investment opportunities.',
      thumbnail: 'https://images.unsplash.com/photo-1676647018922-9b9e8ec7622d?crop=entropy&cs=srgb&fm=jpg&q=85&w=800',
      videoUrl: '/fbg-investment-turkish.mp4',
      language: 'tr'
    },
    {
      id: 'fbg-turkish',
      title: language === 'fr' ? 'Présentation de First Business Group (Turc)' : 'First Business Group Presentation (Turkish)',
      description: language === 'fr' 
        ? 'Présentation complète de First Business Group SARLU en turc.'
        : 'Complete presentation of First Business Group SARLU in Turkish.',
      thumbnail: 'https://images.unsplash.com/photo-1581374820531-029275791beb?crop=entropy&cs=srgb&fm=jpg&q=85&w=800',
      videoUrl: '/fbg-turkish.mp4',
      language: 'tr'
    },
    {
      id: 'fbg-korean',
      title: language === 'fr' ? 'Présentation de First Business Group (Coréen)' : 'First Business Group Presentation (Korean)',
      description: language === 'fr' 
        ? 'Présentation complète de First Business Group SARLU en coréen.'
        : 'Complete presentation of First Business Group SARLU in Korean.',
      thumbnail: 'https://images.unsplash.com/photo-1709803857154-d20ee16ff763?crop=entropy&cs=srgb&fm=jpg&q=85&w=800',
      videoUrl: '/fbg-korean.mp4',
      language: 'ko'
    }
  ];

  const openVideo = (video) => {
    setSelectedVideo(video);
  };

  const closeVideo = () => {
    setSelectedVideo(null);
  };

  return (
    <div className="min-h-screen">
      <section 
        className="relative h-96 flex items-center justify-center overflow-hidden"
        data-testid="video-presentations-hero"
        style={{
          backgroundImage: 'url(https://images.unsplash.com/photo-1478737270239-2f02b77fc618?crop=entropy&cs=srgb&fm=jpg&q=85)',
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
      >
        <div className="absolute inset-0 hero-overlay"></div>
        <div className="relative z-10 text-center text-white px-4">
          <motion.h1 
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="font-serif text-4xl sm:text-5xl font-bold mb-4"
          >
            {language === 'fr' ? 'Présentations Vidéo' : 'Video Presentations'}
          </motion.h1>
          <motion.p 
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="text-lg max-w-2xl mx-auto opacity-90"
          >
            {language === 'fr' 
              ? 'Découvrez First Business Group et les opportunités en RDC'
              : 'Discover First Business Group and DRC opportunities'}
          </motion.p>
        </div>
      </section>

      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-background" data-testid="video-grid">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-8">
            {videos.map((video, idx) => (
              <motion.div
                key={video.id}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: idx * 0.1 }}
                viewport={{ once: true }}
                className="bg-card border border-border/40 overflow-hidden hover:border-primary/50 transition-all hover:shadow-lg group cursor-pointer"
                onClick={() => openVideo(video)}
                data-testid={`video-${video.id}`}
              >
                <div className="relative h-64 overflow-hidden">
                  <img 
                    src={video.thumbnail} 
                    alt={video.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                  />
                  <div className="absolute inset-0 bg-primary/20 group-hover:bg-primary/40 transition-colors flex items-center justify-center">
                    <div className="bg-white rounded-full p-4 group-hover:scale-110 transition-transform">
                      <Play className="h-8 w-8 text-primary" fill="currentColor" />
                    </div>
                  </div>
                  <div className="absolute top-4 right-4 bg-black/60 text-white text-xs px-3 py-1 rounded-full font-mono">
                    {video.language.toUpperCase()}
                  </div>
                </div>
                <div className="p-6">
                  <h3 className="font-serif text-xl font-bold mb-3 text-foreground group-hover:text-primary transition-colors">
                    {video.title}
                  </h3>
                  <p className="text-muted-foreground leading-relaxed">
                    {video.description}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Video Modal */}
      <AnimatePresence>
        {selectedVideo && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4"
            onClick={closeVideo}
            data-testid="video-modal"
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="relative w-full max-w-6xl"
              onClick={(e) => e.stopPropagation()}
            >
              <button
                onClick={closeVideo}
                className="absolute -top-12 right-0 text-white hover:text-secondary transition-colors"
                data-testid="close-video-btn"
              >
                <X className="h-8 w-8" />
              </button>
              <div className="bg-black rounded-lg overflow-hidden">
                <video
                  controls
                  autoPlay
                  className="w-full"
                  data-testid="video-player"
                >
                  <source src={selectedVideo.videoUrl} type="video/mp4" />
                  Your browser does not support the video tag.
                </video>
              </div>
              <div className="mt-4 text-white">
                <h3 className="font-serif text-2xl font-bold mb-2">{selectedVideo.title}</h3>
                <p className="text-white/80">{selectedVideo.description}</p>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default VideoPresentations;
